import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'; // 1. Import Router here
import LoginPage from './components/LoginPage';
import DashboardPage from './components/DashboardPage';
import { getMenuConfig } from './menuConfig'; // Corrected: Use named import
import './App.css';
import { ToastContainer, toast } from 'react-toastify'; // 2. Import toast
import 'react-toastify/dist/ReactToastify.css';
import SessionTimeoutModal from './components/common/SessionTimeoutModal';
import ChangePasswordModal from './components/auth/ChangePasswordModal';
import PayablesNotification from './components/accounts/PayablesNotification';
import ClientPortalApp from './components/client/ClientPortalApp';
import TransporterPortalApp from './components/transporter/TransporterPortalApp';
import { PermissionsProvider, usePermissions } from './services/PermissionsContext';
import { authStorage, isImpersonating } from './auth';

const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 minutes
const WARNING_BEFORE = 30 * 1000; // 30 seconds before logout
const COUNTDOWN_SECONDS = 30; // 30 seconds countdown
// Shared cross-tab heartbeat: since kk_token lives in localStorage (one
// session for every tab), inactivity must also be measured across all
// tabs — otherwise an idle tab logs out an actively-used sibling.
const ACTIVITY_KEY = 'kk_last_activity';

// 3. RENAME the main component from 'App' to 'AppLayout'.
// Detect a "login as user" impersonation handoff from the URL (opened via
// window.open from UserManagement) and stash the tokens in sessionStorage
// (isolated per-tab) before the normal auth bootstrap runs.
if (typeof window !== 'undefined') {
  const impersonateParams = new URLSearchParams(window.location.search);
  if (impersonateParams.get('impersonate') === '1' && impersonateParams.get('access')) {
    try {
      sessionStorage.setItem('kk_impersonating', '1');
      sessionStorage.setItem('kk_token', impersonateParams.get('access'));
      if (impersonateParams.get('refresh')) {
        sessionStorage.setItem('kk_refresh_token', impersonateParams.get('refresh'));
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[kk impersonation] failed to seed sessionStorage:', e);
    }
  }

  // Many components across the app read auth data via a direct
  // `localStorage.getItem('kk_...')` call instead of the `authStorage`
  // helper. Rather than touching every call site, transparently redirect
  // ALL `kk_*` localStorage access to sessionStorage for the lifetime of
  // this window/tab when it is an impersonation popout. We shadow the
  // methods on the localStorage instance (not Storage.prototype) so this
  // cannot affect the admin's own tab.
  if (sessionStorage.getItem('kk_impersonating') === '1' && !window.__kkImpersonationStoragePatched) {
    window.__kkImpersonationStoragePatched = true;
    try {
      const nativeGetItem = Storage.prototype.getItem;
      const nativeSetItem = Storage.prototype.setItem;
      const nativeRemoveItem = Storage.prototype.removeItem;

      const redirect = (fn) => (key, ...args) => {
        if (typeof key === 'string' && key.startsWith('kk_')) {
          return fn.call(window.sessionStorage, key, ...args);
        }
        return fn.call(window.localStorage, key, ...args);
      };

      Object.defineProperty(window.localStorage, 'getItem', { value: redirect(nativeGetItem), writable: true, configurable: true });
      Object.defineProperty(window.localStorage, 'setItem', { value: redirect(nativeSetItem), writable: true, configurable: true });
      Object.defineProperty(window.localStorage, 'removeItem', { value: redirect(nativeRemoveItem), writable: true, configurable: true });
      // eslint-disable-next-line no-console
      console.log('[kk impersonation] localStorage redirected to sessionStorage.');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[kk impersonation] failed to patch localStorage:', e);
    }
  }
}

function AppLayout() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [userid, setUserid] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [authChecked, setAuthChecked] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false); // 4. Add state for the password modal
  const [maintenanceStatus, setMaintenanceStatus] = useState(null);
  const [maintenanceSeconds, setMaintenanceSeconds] = useState(0);
  const [maintenanceBusy, setMaintenanceBusy] = useState(false);
  const [maintenanceGraceActive, setMaintenanceGraceActive] = useState(false);
  const [maintenanceWarningShown, setMaintenanceWarningShown] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const resetInactivityTimerRef = useRef(null);
  // Last activity across ALL tabs (seeded from the shared heartbeat).
  const lastActivityRef = useRef(
    Number(localStorage.getItem(ACTIVITY_KEY)) || Date.now()
  );
  const { permissions, setPermissions, hasPermission } = usePermissions();
  const isSuperAdmin = !!permissions?.is_superuser;

  const fetchMaintenanceStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/login/maintenance-status/');
      if (!res.ok) return null;
      const data = await res.json();
      setMaintenanceStatus(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  // --- FIX: Define refreshAuthToken to keep session alive ---
  const refreshAuthToken = useCallback(async () => {
    const refreshToken = authStorage.getItem('kk_refresh_token');
    if (!refreshToken) return;

    try {
      const res = await fetch('/api/login/refresh/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: refreshToken })
      });
      const data = await res.json();
      if (res.ok) {
        authStorage.setItem('kk_token', data.access);
        if (data.refresh) authStorage.setItem('kk_refresh_token', data.refresh);
      } else {
        // If refresh fails (e.g. refresh token expired), let the interceptor or logout handle it
        // We don't force logout here immediately to avoid race conditions, 
        // but the next API call will fail and trigger the modal.
      }
    } catch (error) {
      console.error("Token refresh failed", error);
    }
  }, []);

  // --- FIX: Modify handleContinueSession to also refresh token ---
  const handleContinueSession = async () => {
    // Mark shared activity so sibling tabs also cancel their countdowns.
    lastActivityRef.current = Date.now();
    try { localStorage.setItem(ACTIVITY_KEY, String(lastActivityRef.current)); } catch {}
    setShowTimeoutModal(false);
    setSecondsLeft(COUNTDOWN_SECONDS);
    await refreshAuthToken();
  };

  // 5. Create function to check password status
  const checkPasswordChangeRequired = useCallback(async () => {
    const token = authStorage.getItem('kk_token');
    if (!token) return;

    try {
      const res = await fetch('/api/admin/password-change-required/', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.must_change_password) {
          setMustChangePassword(true);
        }
      } else {
        // Handle cases where the check fails, e.g., token is invalid
        console.error('Password status check failed:', res.statusText);
      }
    } catch (error) {
      console.error("Could not check password status:", error);
    }
  }, []);

  // --- FIX #1: Modify handleLoginSuccess to accept the full data object ---
  const handleLoginSuccess = useCallback(async (data) => {
    // Always store the new access token
    authStorage.setItem('kk_token', data.access);

    // --- THE DEFINITIVE FIX ---
    // Only update the refresh token if the server sends a new one.
    // This prevents overwriting a valid token with 'undefined' during a normal refresh.
    if (data.refresh) {
      authStorage.setItem('kk_refresh_token', data.refresh);
    }
    
    // Store user details (these are always present on success)
    authStorage.setItem('kk_user', data.user);
    authStorage.setItem('kk_user_id', data.id);
    authStorage.setItem('kk_first_name', data.first_name || '');
    authStorage.setItem('kk_last_name', data.last_name || '');
    authStorage.setItem('kk_email', data.email || '');
    authStorage.setItem('kk_is_superuser', data?.permissions?.is_superuser ? '1' : '0');

    // Set state from the data object
    setIsAuthenticated(true);
    setUsername(data.user);
    setUserid(data.id);
    setFirstName(data.first_name || '');
    setLastName(data.last_name || '');
    setEmail(data.email || '');
    setPermissions(data.permissions || {});

    const lastRoute = authStorage.getItem('kk_last_route');
    if (!lastRoute || lastRoute === '/login' || lastRoute === '/') {
      authStorage.setItem('kk_last_route', '/dashboard');
    }

    await checkPasswordChangeRequired();
  }, [setPermissions]);

  // Logout handler
  // --- FIX: Wrap handleLogout in useCallback to stabilize its reference ---
  const handleLogout = useCallback(() => {
    setShowTimeoutModal(false);
    setSecondsLeft(COUNTDOWN_SECONDS);
    authStorage.removeItem('kk_token');
    authStorage.removeItem('kk_refresh_token'); // Also remove the refresh token
    authStorage.removeItem('kk_user');
    authStorage.removeItem('kk_user_id');
    authStorage.removeItem('kk_first_name');
    authStorage.removeItem('kk_last_name');
    authStorage.removeItem('kk_email');
    authStorage.removeItem('kk_is_superuser');
    try { authStorage.removeItem('kk_last_route'); } catch {}
    if (isImpersonating()) {
      try { sessionStorage.removeItem('kk_impersonating'); } catch {}
    }
    
    setIsAuthenticated(false);
    setUsername('');
    setUserid('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setMustChangePassword(false);
    setPermissions({});
    
    // No need to navigate here, the routing logic will handle it automatically
    // when isAuthenticated becomes false.
  }, [setPermissions]); // setPermissions is stable, so this function is created only once.

  const startMaintenanceLogout = useCallback(async () => {
    const token = authStorage.getItem('kk_token');
    if (!token) return;

    setMaintenanceBusy(true);
    try {
      const res = await fetch('/api/login/maintenance/start/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ countdown_seconds: 30 }),
      });

      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail || 'Failed to start maintenance mode.');
        return;
      }

      setMaintenanceStatus(data);
      setMaintenanceSeconds(data?.seconds_left || 30);
      toast.warn('Logout warning sent. Users will be logged out in 30 seconds.');
    } catch {
      toast.error('Failed to start maintenance mode.');
    } finally {
      setMaintenanceBusy(false);
    }
  }, []);

  const stopMaintenanceLogout = useCallback(async () => {
    const token = authStorage.getItem('kk_token');
    if (!token) return;

    setMaintenanceBusy(true);
    try {
      const res = await fetch('/api/login/maintenance/stop/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail || 'Failed to stop maintenance mode.');
        return;
      }

      setMaintenanceStatus(data);
      setMaintenanceSeconds(0);
      toast.success('Maintenance mode disabled. Logins are enabled.');
    } catch {
      toast.error('Failed to stop maintenance mode.');
    } finally {
      setMaintenanceBusy(false);
    }
  }, []);

  // Initial auth load
  useEffect(() => {
    const initializeAuth = async () => {
      const today = new Date().toISOString().split('T')[0];
      // While impersonating, skip the 'new day' route-reset logic entirely so
      // the popout session never touches the admin's own localStorage state.
      const isNewDay = !isImpersonating() && (() => {
        const lastAppDate = localStorage.getItem('kk_last_app_date');
        return lastAppDate && lastAppDate !== today;
      })();

      // On the first visit of a new day, reset the persisted route so the user
      // starts fresh at the login screen / dashboard instead of yesterday's page.
      if (isNewDay) {
        authStorage.removeItem('kk_last_route');
        authStorage.removeItem('kk_dashboard_view');
        handleLogout();
      }
      if (!isImpersonating()) {
        localStorage.setItem('kk_last_app_date', today);
      }

      const token = authStorage.getItem('kk_token');
      if (token && !isNewDay) {
        try {
          // Re-validate the token and fetch user data on load
          const res = await fetch('/api/login/refresh/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh: authStorage.getItem('kk_refresh_token') })
          });

          const data = await res.json();
          if (res.ok) {
            // If refresh is successful, treat it like a new login
            await handleLoginSuccess(data);
          } else {
            // If refresh fails, clear auth
            handleLogout();
          }
        } catch (e) {
          console.warn('Auth init error:', e);
          handleLogout();
        } finally {
          // --- FIX: Move setAuthChecked into a finally block ---
          // This ensures it runs even if the fetch fails.
          setAuthChecked(true);
        }
      } else {
        // If there's no token or it's a new day, just mark auth as checked.
        setAuthChecked(true);
      }
    };
    initializeAuth();
  }, [handleLoginSuccess, handleLogout]);

  useEffect(() => {
    fetchMaintenanceStatus();
  }, [fetchMaintenanceStatus]);

  useEffect(() => {
    const interval = setInterval(fetchMaintenanceStatus, 2000);
    return () => clearInterval(interval);
  }, [fetchMaintenanceStatus]);

  useEffect(() => {
    if (!isAuthenticated || isSuperAdmin) {
      setMaintenanceSeconds(0);
      setMaintenanceGraceActive(false);
      setMaintenanceWarningShown(false);
      return;
    }

    if (maintenanceStatus?.warning_active) {
      setMaintenanceWarningShown(true);
      setMaintenanceGraceActive(false);
      setMaintenanceSeconds(maintenanceStatus?.seconds_left || 0);
      return;
    }

    if (maintenanceStatus?.locked) {
      // Fallback: if this client missed warning phase, enforce a local warning grace
      // so users always see an alert before logout.
      if (!maintenanceWarningShown && !maintenanceGraceActive) {
        setMaintenanceGraceActive(true);
        setMaintenanceWarningShown(true);
        setMaintenanceSeconds(30);
        toast.warn('Maintenance mode started. You will be logged out in 30 seconds.');
        return;
      }

      if (!maintenanceGraceActive && maintenanceSeconds <= 0) {
        toast.error('You have been logged out for maintenance.');
        handleLogout();
      }
      return;
    }

    setMaintenanceSeconds(0);
    setMaintenanceGraceActive(false);
    setMaintenanceWarningShown(false);
  }, [isAuthenticated, isSuperAdmin, maintenanceStatus, maintenanceSeconds, maintenanceWarningShown, maintenanceGraceActive, handleLogout]);

  useEffect(() => {
    if (!isAuthenticated || isSuperAdmin || maintenanceSeconds <= 0) return;
    const interval = setInterval(() => {
      setMaintenanceSeconds((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated, isSuperAdmin, maintenanceSeconds]);

  useEffect(() => {
    if (!isAuthenticated || isSuperAdmin) return;
    const serverWarningActive = !!maintenanceStatus?.warning_active;
    const serverSecondsLeft = Number(maintenanceStatus?.seconds_left ?? -1);

    // During server warning phase, use backend countdown as source of truth.
    // This avoids a render race where local maintenanceSeconds is still 0.
    if (serverWarningActive) {
      if (serverSecondsLeft <= 0 && maintenanceSeconds <= 0) {
        handleLogout();
      }
      return;
    }

    // Local fallback grace (only when warning phase was missed client-side).
    if (maintenanceGraceActive && maintenanceSeconds <= 0) {
      handleLogout();
    }
  }, [isAuthenticated, isSuperAdmin, maintenanceStatus, maintenanceGraceActive, maintenanceSeconds, handleLogout]);

  useEffect(() => {
    if (location.pathname === '/login') return;

    const handleKeyDown = (e) => {
      // Allow Enter in textarea and when dropdown is open
      if (e.key === 'Enter') {
        // Check if a dropdown or multiselect is open
        const dropdownOpen = document.querySelector('.dropdown-open, .multiselect-options.open');
        if (
          e.target.tagName !== 'TEXTAREA' &&
          e.target.form &&
          e.target.type !== 'submit' &&
          !dropdownOpen
        ) {
          const active = document.activeElement;
          if (
            !(active && active.tagName === 'BUTTON' && active.type === 'submit') &&
            !(active && active.tagName === 'INPUT' && active.type === 'submit')
          ) {
            e.preventDefault();
            return false;
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [location.pathname]);

  // Persist current route only when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    try {
      const full = location.pathname + location.search + location.hash;
      authStorage.setItem('kk_last_route', full);
    } catch {}
  }, [location, isAuthenticated]);

  // Restore last route when auth becomes true (new / duplicated tab)
  useEffect(() => {
    if (!authChecked || !isAuthenticated) return;
    if (location.pathname.startsWith('/dashboard')) return;
    const atEntry = ['/', '/login', '/dashboard'].includes(location.pathname);
    try {
      const last = authStorage.getItem('kk_last_route');
      if (last && atEntry && last !== location.pathname) {
        navigate(last, { replace: true });
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked, isAuthenticated, location.pathname, navigate]);

  // Cross‑tab sync (token + last route)
  useEffect(() => {
    function onStorage(e) {
      // Impersonation sessions run entirely on sessionStorage and must never
      // react to the admin's own localStorage-driven auth changes.
      if (isImpersonating()) return;
      if (e.key === 'kk_token') {
        if (!e.newValue) {
          handleLogout();
          navigate('/login', { replace: true });
        } else {
          // Token added in another tab (login there) -> attempt silent rehydrate here
          if (!isAuthenticated) {
            const u = localStorage.getItem('kk_user');
            if (u) {
              setIsAuthenticated(true);
              setUsername(u);
              setUserid(localStorage.getItem('kk_user_id') || '');
              setFirstName(localStorage.getItem('kk_first_name') || '');
              setLastName(localStorage.getItem('kk_last_name') || '');
              setEmail(localStorage.getItem('kk_email') || '');
            }
          }
        }
      }
      if (e.key === ACTIVITY_KEY && e.newValue) {
        // Activity in another tab — the shared session is still in use, so
        // cancel any pending timeout warning here and restart the timers.
        const shared = Number(e.newValue);
        if (shared > lastActivityRef.current) lastActivityRef.current = shared;
        setShowTimeoutModal(false);
        setSecondsLeft(COUNTDOWN_SECONDS);
        if (resetInactivityTimerRef.current) resetInactivityTimerRef.current();
      }
      if (e.key === 'kk_last_route') {
        if (!isAuthenticated) return;
        const atEntry = ['/', '/login'].includes(location.pathname);
        if (atEntry && e.newValue && e.newValue !== location.pathname) {
            navigate(e.newValue, { replace: true });
        }
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [isAuthenticated, location.pathname, navigate]);

  // --- FIX: Background token refresh every 4 minutes ---
  // This ensures that if the user is active, the token (which lasts 15 mins) 
  // is refreshed before it expires, preventing unnecessary timeouts.
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(refreshAuthToken, 4 * 60 * 1000); 
    return () => clearInterval(interval);
  }, [isAuthenticated, refreshAuthToken]);

  // Session timeout — inactivity is measured across ALL tabs sharing the
  // session (the token lives in shared localStorage). A tab only times out
  // when NO tab has been active within the limit.
  const resetInactivityTimer = useCallback(() => {
    // Clear all existing timers
    clearTimeout(inactivityTimerRef.current);
    clearTimeout(warningTimerRef.current);
    clearInterval(countdownRef.current);

    // Don't set new timers if user is not authenticated or modal is already showing
    if (!isAuthenticated || showTimeoutModal) {
      return;
    }

    // Timer to show the warning modal
    inactivityTimerRef.current = setTimeout(() => {
      // Another tab was active recently — the shared session is still in
      // use, so reschedule instead of showing the timeout warning.
      if (Date.now() - lastActivityRef.current < INACTIVITY_LIMIT - COUNTDOWN_SECONDS * 1000) {
        resetInactivityTimer();
        return;
      }
      setSecondsLeft(COUNTDOWN_SECONDS);
      setShowTimeoutModal(true);
    }, INACTIVITY_LIMIT - (COUNTDOWN_SECONDS * 1000));

    // Timer to force logout if there's no activity at all in any tab
    warningTimerRef.current = setTimeout(() => {
      if (Date.now() - lastActivityRef.current < INACTIVITY_LIMIT) {
        resetInactivityTimer();
        return;
      }
      handleLogout();
    }, INACTIVITY_LIMIT);
  }, [isAuthenticated, showTimeoutModal, handleLogout]);

  useEffect(() => {
    resetInactivityTimerRef.current = resetInactivityTimer;
  }, [resetInactivityTimer]);

  useEffect(() => {
    const onActivity = () => {
      // Throttled shared heartbeat so sibling tabs know this session is
      // still being used somewhere.
      const now = Date.now();
      if (now - lastActivityRef.current > 5000) {
        lastActivityRef.current = now;
        try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch {}
      }
      resetInactivityTimer();
    };

    // Add event listeners to reset timers on user activity
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
    events.forEach(event => window.addEventListener(event, onActivity));

    // Initial timer start
    onActivity();

    // Cleanup function to remove listeners when component unmounts or auth changes
    return () => {
      events.forEach(event => window.removeEventListener(event, onActivity));
      clearTimeout(inactivityTimerRef.current);
      clearTimeout(warningTimerRef.current);
      clearInterval(countdownRef.current);
    };
  }, [isAuthenticated, showTimeoutModal, resetInactivityTimer]);

  // Countdown effect for the modal
  useEffect(() => {
    if (showTimeoutModal) {
      countdownRef.current = setInterval(() => {
        setSecondsLeft(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            // Final check: another tab may have become active during the
            // countdown — only log out when nothing was active anywhere.
            if (Date.now() - lastActivityRef.current < INACTIVITY_LIMIT) {
              setShowTimeoutModal(false);
              setSecondsLeft(COUNTDOWN_SECONDS);
              if (resetInactivityTimerRef.current) resetInactivityTimerRef.current();
            } else {
              handleLogout();
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(countdownRef.current);
  }, [showTimeoutModal, handleLogout]);

  // Token expiry detection (fetch wrapper)
  useEffect(() => {
    if (!isAuthenticated) return;

    // Patch window.fetch to intercept 401s
    const origFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await origFetch(...args);
      if (response.status === 401) {
        // Show warning modal if not already shown
        if (!showTimeoutModal) {
          setShowTimeoutModal(true);
          setSecondsLeft(COUNTDOWN_SECONDS);
          // The existing useEffects will handle the countdown and logout
        }
      }
      return response;
    };
    return () => {
      window.fetch = origFetch;
    };
  }, [isAuthenticated, showTimeoutModal]);

  // 10. Add handler for when password is changed successfully
  const handlePasswordChanged = () => {
    setMustChangePassword(false);
    toast.success("Password changed successfully! Please log in again with your new password.");
    // Force re-login for security
    setTimeout(() => {
      handleLogout();
    }, 1500);
  };

  // While auth not yet checked, render nothing
  if (!authChecked) {
    return (
      <div style={{
        display:'flex',
        alignItems:'center',
        justifyContent:'center',
        height:'100vh',
        fontFamily:'sans-serif'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="app-root-container">
      {/* 11. Render the password change modal conditionally */}
      {isAuthenticated && mustChangePassword && <ChangePasswordModal onPasswordChanged={handlePasswordChanged} />}

      <Routes>
        <Route
          path="/login"
          element={
            !isAuthenticated ? (
              <LoginPage onLoginSuccess={handleLoginSuccess} maintenanceStatus={maintenanceStatus} />
            ) : (
              // This line reads the value set above and navigates
              <Navigate to={authStorage.getItem('kk_last_route') || '/dashboard'} replace />
            )
          }
        />
        <Route
          path="/dashboard/*"
          element={
            isAuthenticated ? (
              <DashboardPage
                username={username}
                firstName={firstName}
                lastName={lastName}
                onLogout={handleLogout}
                menuData={getMenuConfig(hasPermission)} // Call the function here
                hasPermission={hasPermission} // Pass the function as a prop
                isSuperAdmin={isSuperAdmin}
                maintenanceStatus={maintenanceStatus}
                maintenanceBusy={maintenanceBusy}
                onStartMaintenance={startMaintenanceLogout}
                onStopMaintenance={stopMaintenanceLogout}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route path="/client/*" element={<ClientPortalApp />} />
        <Route path="/transporter/*" element={<TransporterPortalApp />} />
        <Route
          path="/*"
          element={
            isAuthenticated
              ? <Navigate to={authStorage.getItem('kk_last_route') || '/dashboard'} replace />
              : <Navigate to="/login" replace />
          }
        />
      </Routes>

      <SessionTimeoutModal
        show={showTimeoutModal}
        onStay={handleContinueSession}
        onLogout={handleLogout}
        secondsLeft={secondsLeft}
      />
      {isAuthenticated && <PayablesNotification />}
      {isAuthenticated && !isSuperAdmin && (maintenanceStatus?.warning_active || maintenanceGraceActive) && maintenanceSeconds > 0 && (
        <div style={{
          position: 'fixed',
          top: '12px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1200,
          background: 'linear-gradient(90deg, #dc3545 0%, #b02a37 100%)',
          color: '#fff',
          boxShadow: '0 6px 18px rgba(0,0,0,0.24)',
          borderRadius: '10px',
          width: 'min(940px, calc(100% - 24px))'
        }}>
          <div style={{
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <div style={{ fontSize: '0.95rem', lineHeight: 1.35 }}>
              <strong style={{ marginRight: '8px' }}>Maintenance warning:</strong>
              You will be logged out in <strong>{maintenanceSeconds}</strong> seconds for server/software update.
            </div>
            <button
              onClick={handleLogout}
              style={{
                border: '1px solid rgba(255,255,255,0.7)',
                background: 'rgba(255,255,255,0.12)',
                color: '#fff',
                borderRadius: '6px',
                padding: '6px 12px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Logout Now
            </button>
          </div>
        </div>
      )}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}

// 4. CREATE a new top-level App component to set up providers and routing.
function App() {
  return (
    <PermissionsProvider>
      <Router>
        <AppLayout />
      </Router>
    </PermissionsProvider>
  );
}

export default App; // 5. EXPORT the new App component as the default.